import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { GetTransmissionEventsQuery } from '../data/queries/getTransmissionEventsQuery.gql';
import type { TransmissionEvent } from '../data/utils/types';

// Type for the query response
type GetTransmissionEventsQueryResponse = {
  transmissionEvents?: TransmissionEvent[];
};

/**
 * Hook to fetch transmission events for an entity (order/email)
 * Used to track delivery status and failures
 *
 * @param entityId - The ID of the entity to fetch transmission events for
 * @param onError - Optional error handler
 * @returns Transmission events, loading state, and error
 *
 * @example
 * ```tsx
 * const { transmissionEvents, loading, error } = useGetTransmissionEvents('order-123');
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return (
 *   <TransmissionEventsList events={transmissionEvents} />
 * );
 * ```
 */
export const useGetTransmissionEvents = (
  entityId: string,
  onError?: (error: Error) => void
) => {
  const { data, loading, error } = useQuery<GetTransmissionEventsQueryResponse>(
    GetTransmissionEventsQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: { ofId: entityId },
      onError,
      skip: !entityId,
    }
  );

  const transmissionEvents = useMemo<TransmissionEvent[]>(() => {
    if (!data?.transmissionEvents) return [];
    return data.transmissionEvents as TransmissionEvent[];
  }, [data?.transmissionEvents]);

  return {
    transmissionEvents,
    loading: loading || (!data && !error),
    error: error || null,
  };
};
